import type { FollowUpQueue, FollowUpSummary } from '@/lib/api-client'
import { cn, formatCurrency } from '@/lib/utils'
import { QUEUE_META, QUEUE_ORDER } from './queue-meta'

interface QueueTilesProps {
  summary: FollowUpSummary | undefined
  isPending: boolean
  selected: FollowUpQueue | null
  onSelect: (queue: FollowUpQueue | null) => void
}

/** Six colored tiles — one per queue — that double as the queue filter. */
export function QueueTiles({ summary, isPending, selected, onSelect }: QueueTilesProps) {
  return (
    <div className="grid grid-cols-2 sm:grid-cols-3 xl:grid-cols-6 gap-2.5">
      {QUEUE_ORDER.map((queue) => {
        const meta = QUEUE_META[queue]
        const stats = summary?.queues[queue]
        const isOn = selected === queue
        return (
          <button
            key={queue}
            type="button"
            onClick={() => onSelect(isOn ? null : queue)}
            className={cn(
              'flex flex-col gap-2 rounded-2xl border bg-card p-3.5 text-left shadow-sm transition-colors',
              isOn ? 'border-text-dark ring-1 ring-text-dark' : 'border-border hover:bg-hover/50'
            )}
          >
            <span className="flex items-center gap-1.5 text-[11px] font-bold uppercase tracking-wide text-text-muted">
              <span className={cn('h-2 w-2 rounded-full', meta.dotClassName)} />
              {meta.label}
            </span>
            <span className="text-[26px] font-extrabold leading-none tracking-tight">
              {isPending ? '—' : (stats?.total ?? 0)}
            </span>
            <span className="text-xs text-text-muted">
              {isPending ? (
                'Loading...'
              ) : queue === 'no_show' || queue === 'consult_no_purchase' ? (
                <>
                  <strong className="text-text-dark">{stats?.pending ?? 0}</strong> not contacted
                </>
              ) : (
                <>
                  <strong className="text-text-dark">{formatCurrency(stats?.value ?? 0)}</strong>{' '}
                  {queue === 'lapsed_regular'
                    ? 'lifetime spend'
                    : queue === 'package_done'
                      ? 'last package price'
                      : 'unredeemed'}
                </>
              )}
            </span>
            <span className="text-[11px] leading-snug text-text-light">{meta.description}</span>
          </button>
        )
      })}
    </div>
  )
}
